import { ReactNode } from "react";
import { Col, Row } from "reactstrap";
import { P } from "../../../../AbstractElements";
import {
  employeeFormatValueDefault,
  employeeToTitleCase,
} from "../../utils/fieldFormatter";
import { employeeGetOrderedEntries } from "../../utils/format";

export type EmployeeValueFormatter<T extends object> = (
  value: unknown,
  data: T
) => ReactNode;

export interface EmployeeFieldGridProps<T extends object> {
  data: T;
  fieldOrder?: (keyof T | string)[];
  labelMap?: Partial<Record<keyof T | string, string>>;
  formatters?: Partial<Record<keyof T | string, EmployeeValueFormatter<T>>>;
  omitKeys?: (keyof T | string)[];
  colMd?: 6 | 12 | 3 | 4; // default 6
  fieldActions?: Partial<Record<keyof T | string, () => void>>;
  canEdit?: boolean;
  previewableKeys?: (keyof T | string)[];
  onPreview?: (key: keyof T | string, value: unknown) => void;
}

const EmployeeFieldGrid = <T extends object>({
  data,
  fieldOrder,
  labelMap,
  formatters,
  omitKeys,
  colMd = 6,
  fieldActions,
  canEdit,
  previewableKeys,
  onPreview,
}: EmployeeFieldGridProps<T>) => {
  const entries = employeeGetOrderedEntries<T>(data, omitKeys, fieldOrder);

  return (
    <Row>
      {entries.map(([key, value], idx) => {
        const label = (labelMap?.[key] as string) ?? employeeToTitleCase(key);
        const render =
          (formatters?.[key] as EmployeeValueFormatter<T>) ??
          employeeFormatValueDefault;
        const action = fieldActions?.[key];
        const hasValue = value !== null && value !== undefined && value !== "";

        const isPreviewable = previewableKeys?.includes(key);

        return (
          <Col md={colMd} key={idx}>
            <div className="d-flex align-items-center">
              <div>
                <P className="mb-1 f-12 text-capitalize">{label}</P>
                <div className="d-flex align-items-center">
                  {/* Show value only if not previewable */}
                  {!isPreviewable && (
                    <div className="mb-2 f-12 text-gray">
                      {render(value, data)}
                    </div>
                  )}

                  {/* Edit icon (if allowed) */}
                  {action && canEdit && (
                    <i
                      className="fa fa-edit pointer f-20 text-primary ms-2 mb-2"
                      onClick={action}
                    />
                  )}

                  {/* 👁 Preview icon for previewable fields */}
                  {isPreviewable && hasValue && (
                    <i
                      className="fa fa-eye pointer f-20 text-primary ms-2 mb-2"
                      onClick={() => onPreview?.(key, value)}
                    />
                  )}
                </div>
              </div>
            </div>
          </Col>
        );
      })}
    </Row>
  );
};

export default EmployeeFieldGrid;
